/**
 * Nearest Store Hook
 * Finds the nearest store to user location and manages store selection
 */
import { useState, useEffect, useCallback } from 'react';
import { firestore } from '../../../../../libs/firebase-service/src';
import { calculateDistance } from '../services/locationService';

export const useNearestStore = ({
  userLocation,
  autoSelectNearest = true,
  enableConsoleLogging = false,
}) => {
  const [stores, setStores] = useState([]);
  const [nearestStore, setNearestStore] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [storeError, setStoreError] = useState(null);

  const log = useCallback(
    (...args) => {
      if (enableConsoleLogging) {
        console.log('[useNearestStore]', ...args);
      }
    },
    [enableConsoleLogging]
  );

  // Fetch all stores from Firebase
  const fetchStores = useCallback(async () => {
    try {
      setIsLoadingStores(true);
      setStoreError(null);
      // log('Fetching stores from Firebase...');

      const storesSnapshot = await firestore
        .collection('stores')
        .where('is_deleted', '==', false)
        .get();

      const storesData = storesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // log(`Found ${storesData.length} stores`);
      setStores(storesData);
      return storesData;
    } catch (error) {
      // log('Error fetching stores:', error);
      setStoreError('Failed to load stores');
      return [];
    } finally {
      setIsLoadingStores(false);
    }
  }, [log]);

  // Calculate distances and find nearest store
  const findNearestStore = useCallback(
    (storesArray, location) => {
      if (!storesArray?.length || !location?.lat || !location?.lng) {
        return null;
      }

      // log('Calculating distances to stores...');

      const storesWithDistance = storesArray
        .map((store) => {
          // Get store coordinates from _geoloc or address
          const storeLocation = store._geoloc || store.address;
          const distance = calculateDistance(location, storeLocation);

          if (distance === null) {
            // log(`Store ${store.businessName} has invalid coordinates`);
            return null;
          }

          return {
            ...store,
            distance: distance,
            storeLocation: {
              lat: storeLocation.lat,
              lng: storeLocation.lng,
            },
          };
        })
        .filter(Boolean) // Remove stores with invalid coordinates
        .sort((a, b) => a.distance - b.distance);

      if (storesWithDistance.length > 0) {
        const nearest = storesWithDistance[0];
        // log(`Nearest store: ${nearest.businessName} (${nearest.distance.toFixed(2)}km away)`);
        return nearest;
      }

      return null;
    },
    [log]
  );

  // Update nearest store when location or stores change
  useEffect(() => {
    if (stores.length > 0 && userLocation) {
      const nearest = findNearestStore(stores, userLocation);
      setNearestStore(nearest);

      if (autoSelectNearest && nearest && !selectedStore) {
        setSelectedStore(nearest);
        // log(`Auto-selected nearest store: ${nearest.businessName}`);
      }
    }
  }, [
    stores,
    userLocation,
    autoSelectNearest,
    selectedStore,
    findNearestStore,
    log,
  ]);

  // Initialize stores on mount
  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  // Manually select a store
  const selectStore = useCallback(
    (store) => {
      setSelectedStore(store);
      // log(`Manually selected store: ${store.businessName}`);
    },
    [log]
  );

  // Get all stores sorted by distance
  const getStoresByDistance = useCallback(() => {
    if (!stores.length || !userLocation) {
      // log('No stores or user location available for sorting');
      return stores;
    }

    // log(`Sorting ${stores.length} stores by distance from location:`, userLocation);

    const sortedStores = stores
      .map((store) => {
        const storeLocation = store._geoloc || store.address;
        const distance = calculateDistance(userLocation, storeLocation);

        if (distance !== null) {
          // log(`Store: ${store.businessName || store.name} - Distance: ${distance.toFixed(2)}km`);
        } else {
          // log(`Store: ${store.businessName || store.name} - Invalid coordinates`);
        }

        return { ...store, distance };
      })
      .filter((store) => store.distance !== null)
      .sort((a, b) => a.distance - b.distance);

    // log('Final sorted order:');
    sortedStores.forEach((store, index) => {
      // log(`${index + 1}. ${store.businessName || store.name} (${store.distance.toFixed(2)}km)${store.isTestData ? ' [TEST]' : ''}`);
    });

    return sortedStores;
  }, [stores, userLocation, log]);

  // Refresh stores
  const refreshStores = useCallback(() => {
    fetchStores();
  }, [fetchStores]);

  return {
    // Store data
    stores,
    nearestStore,
    selectedStore,

    // State
    isLoadingStores,
    storeError,

    // Actions
    selectStore,
    refreshStores,
    getStoresByDistance,

    // Utils
    findNearestStore,
  };
};

export default useNearestStore;
